import { CONTRAST_ENUM } from "../pages/SettingsPage/constants/constants";
import type { UserInfoState } from "./userInfoSlice";

export const USER_SETTINGS_STORAGE_KEY = "userSettings";

export type PersistedUserSettings = Pick<
  UserInfoState,
  "timerSound" | "soundEffects" | "fontSize" | "contrast" | "showPictures"
>;

const FONT_SIZES: PersistedUserSettings["fontSize"][] = [
  "small",
  "standard",
  "large",
  "very-large",
];

const CONTRAST_VALUES = new Set<number>([
  CONTRAST_ENUM.STANDARD,
  CONTRAST_ENUM.WHITE,
  CONTRAST_ENUM.DARK,
]);

const LEGACY_PII_KEYS = ["userData", "email", "userName", "userId"] as const;

export const pickPersistedUserSettings = (
  state: UserInfoState,
): PersistedUserSettings => ({
  timerSound: state.timerSound,
  soundEffects: state.soundEffects,
  fontSize: state.fontSize,
  contrast: state.contrast,
  showPictures: state.showPictures,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const parsePersistedUserSettings = (
  raw: unknown,
): Partial<PersistedUserSettings> => {
  if (!isRecord(raw)) return {};

  const settings: Partial<PersistedUserSettings> = {};

  if (typeof raw.timerSound === "boolean") {
    settings.timerSound = raw.timerSound;
  }
  if (typeof raw.soundEffects === "boolean") {
    settings.soundEffects = raw.soundEffects;
  }
  if (typeof raw.showPictures === "boolean") {
    settings.showPictures = raw.showPictures;
  }
  if (
    typeof raw.fontSize === "string" &&
    FONT_SIZES.includes(raw.fontSize as PersistedUserSettings["fontSize"])
  ) {
    settings.fontSize = raw.fontSize as PersistedUserSettings["fontSize"];
  }
  if (typeof raw.contrast === "number" && CONTRAST_VALUES.has(raw.contrast)) {
    settings.contrast = raw.contrast as CONTRAST_ENUM;
  }

  return settings;
};

const hasLegacyPersistedFields = (raw: unknown): boolean =>
  isRecord(raw) && LEGACY_PII_KEYS.some((key) => key in raw);

export const loadPersistedUserSettings = (): Partial<PersistedUserSettings> => {
  if (typeof window === "undefined") return {};

  try {
    const savedSettings = localStorage.getItem(USER_SETTINGS_STORAGE_KEY);
    if (!savedSettings) return {};

    const parsed: unknown = JSON.parse(savedSettings);
    const settings = parsePersistedUserSettings(parsed);

    if (hasLegacyPersistedFields(parsed)) {
      savePersistedUserSettings(settings);
    }

    return settings;
  } catch (error) {
    console.error("Error loading settings from localStorage:", error);
    return {};
  }
};

export const savePersistedUserSettings = (
  settings: Partial<PersistedUserSettings>,
): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error("Error saving settings to localStorage:", error);
  }
};
